import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Leaf, Minus, Plus, ShoppingBag } from "lucide-react";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { CatalogWarming } from "./Home";
import { Reveal } from "@/components/Reveal";
import { useProduct, useProducts } from "@/lib/queries";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";

export default function ProductDetail() {
    const { slug } = useParams();
    const { data, isLoading, isError, refetch } = useProduct(slug);
    const { addItem, setDrawerOpen } = useCart();
    const product = data?.product;

    const groups = useMemo(() => {
        if (!product || product.type !== "variable") return [];
        const map = new Map();
        (product.variations || []).forEach((v) => {
            Object.entries(v.attributes || {}).forEach(([name, value]) => {
                if (!map.has(name)) map.set(name, []);
                if (!map.get(name).includes(value)) map.get(name).push(value);
            });
        });
        return Array.from(map.entries()).map(([name, options]) => ({ name, options }));
    }, [product]);

    const [selected, setSelected] = useState({});
    const [qty, setQtyLocal] = useState(1);
    const [imgIndex, setImgIndex] = useState(0);

    const selectedVariation = useMemo(() => {
        if (!product || product.type !== "variable" || groups.length === 0) return null;
        return (product.variations || []).find((v) => groups.every((g) => (v.attributes || {})[g.name] === selected[g.name])) || null;
    }, [product, groups, selected]);

    if (isLoading) {
        return (
            <div className="container-x pt-32 pb-24 grid gap-10 lg:grid-cols-2" data-testid="product-loading">
                <div className="skeleton aspect-square rounded-3xl" />
                <div className="space-y-5 pt-4">
                    <div className="skeleton h-8 w-3/4 rounded-xl" />
                    <div className="skeleton h-6 w-1/3 rounded-xl" />
                    <div className="skeleton h-24 rounded-xl" />
                    <div className="skeleton h-14 w-64 rounded-full" />
                </div>
            </div>
        );
    }

    if (isError || !product) {
        return (
            <div className="container-x pt-40 pb-24 text-center" data-testid="product-not-found">
                <h1 className="font-display text-3xl font-extrabold text-bone">We couldn't find that one.</h1>
                <p className="mt-3 text-sage">It may have sold out or moved. The rest of the menu is waiting.</p>
                <div className="mt-8">
                    <CatalogWarming onRetry={refetch} />
                </div>
                <Link to="/shop" data-testid="product-back-to-shop" className="mt-8 inline-block rounded-full bg-leaf px-8 py-4 font-bold text-ink hover:bg-leaf-bright">
                    Back to the Menu
                </Link>
            </div>
        );
    }

    const isVariable = product.type === "variable";
    const variation = selectedVariation;
    const active = variation || product;
    const price = active.price;
    const regular = active.regular_price || active.price;
    const onSale = active.on_sale || product.on_sale;
    const outOfStock = variation
        ? variation.stock_status === "outofstock" || !variation.purchasable
        : !product.is_in_stock || !product.purchasable;
    const images = product.images || [];

    const handleAdd = () => {
        addItem(product, { variation: variation || undefined, quantity: qty });
        setDrawerOpen(true);
    };

    return (
        <div className="pt-24 md:pt-32 pb-24" data-testid="product-detail-page">
            <Seo
                title={`${product.name} — District Greenz DMV`}
                description={`${product.name} at District Greenz DMV. ${money(price)}${onSale ? ` (on sale from ${money(regular)})` : ""}. Open 24 hours, Mon–Sun, in Washington DC.`}
                path={`/product/${product.slug}`}
            />

            <div className="container-x">
                <nav className="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-sage" data-testid="product-breadcrumb">
                    <Link to="/" className="hover:text-leaf-bright">Home</Link>
                    <ChevronRight size={11} />
                    <Link to="/shop" className="hover:text-leaf-bright">Shop</Link>
                    {product.categories?.[0] && (
                        <>
                            <ChevronRight size={11} />
                            <Link to={`/shop?category=${product.categories[0].slug}`} className="hover:text-leaf-bright">
                                {product.categories[0].name}
                            </Link>
                        </>
                    )}
                    <ChevronRight size={11} />
                    <span className="text-bone/70">{product.name}</span>
                </nav>

                <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:gap-16">
                    <div data-testid="product-gallery">
                        <div className="relative aspect-square overflow-hidden rounded-3xl border border-leaf/15 bg-moss">
                            {images.length > 0 ? (
                                <img
                                    src={images[imgIndex]?.src}
                                    alt={images[imgIndex]?.alt || product.name}
                                    className="h-full w-full object-cover"
                                    data-testid="product-main-image"
                                />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center text-leaf/40">
                                    <Leaf size={48} />
                                </div>
                            )}
                            {onSale && product.discount_percent > 0 && (
                                <span className="absolute left-5 top-5 rounded-full bg-gold px-4 py-1.5 font-mono text-xs font-bold text-ink" data-testid="product-detail-sale-badge">
                                    -{product.discount_percent}%
                                </span>
                            )}
                        </div>
                        {images.length > 1 && (
                            <div className="mt-4 flex gap-3 overflow-x-auto no-scrollbar">
                                {images.map((im, i) => (
                                    <button
                                        key={i}
                                        data-testid={`product-thumb-${i}`}
                                        onClick={() => setImgIndex(i)}
                                        className={`h-20 w-20 shrink-0 overflow-hidden rounded-xl border-2 transition-colors ${
                                            i === imgIndex ? "border-leaf" : "border-transparent opacity-60 hover:opacity-100"
                                        }`}
                                    >
                                        <img src={im.src} alt="" className="h-full w-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        {(product.brand || product.strain) && (
                            <div className="flex flex-wrap gap-2">
                                {product.brand && (
                                    <span className="rounded-full border border-gold/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold" data-testid="product-brand-pill">
                                        {product.brand}
                                    </span>
                                )}
                                {product.strain && (
                                    <span className="rounded-full border border-leaf/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-leaf-bright" data-testid="product-strain-pill">
                                        {product.strain}
                                    </span>
                                )}
                            </div>
                        )}

                        <h1 className="mt-4 font-display text-3xl md:text-5xl font-extrabold leading-tight text-bone" data-testid="product-name">
                            {product.name}
                        </h1>

                        <div className="mt-5 flex items-baseline gap-3" data-testid="product-price-block">
                            <span className={`font-display text-3xl font-extrabold ${onSale ? "text-leaf-bright" : "text-bone"}`} data-testid="product-detail-price">
                                {price != null ? money(price) : "—"}
                            </span>
                            {onSale && regular > price && (
                                <span className="text-lg text-sage line-through">{money(regular)}</span>
                            )}
                            {outOfStock && (
                                <span className="rounded-full border border-red-400/40 px-3 py-1 text-xs font-semibold text-red-400">Sold out</span>
                            )}
                        </div>

                        {product.short_description && (
                            <div
                                className="prose-dg mt-6 text-sm leading-relaxed text-bone/75"
                                dangerouslySetInnerHTML={{ __html: product.short_description }}
                                data-testid="product-short-description"
                            />
                        )}

                        {isVariable && groups.length > 0 && (
                            <div className="mt-8 space-y-5" data-testid="product-variation-picker">
                                {groups.map((g) => (
                                    <div key={g.name}>
                                        <p className="eyebrow text-sage mb-3">{g.name}</p>
                                        <div className="flex flex-wrap gap-2">
                                            {g.options.map((opt) => {
                                                const isSel = selected[g.name] === opt;
                                                return (
                                                    <button
                                                        key={opt}
                                                        data-testid={`product-option-${g.name}-${opt}`.replace(/\s+/g, "-").toLowerCase()}
                                                        onClick={() => setSelected((s) => ({ ...s, [g.name]: opt }))}
                                                        className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                                                            isSel ? "bg-leaf text-ink" : "border border-leaf/25 bg-moss/50 text-bone hover:border-leaf/60"
                                                        }`}
                                                    >
                                                        {opt}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {isVariable && groups.length > 0 && variation && variation.stock_quantity != null && (
                            <p className="mt-4 font-mono text-xs uppercase tracking-widest text-sage" data-testid="product-variation-stock">
                                {variation.stock_quantity > 0 ? `Only ${variation.stock_quantity} left` : "Out of stock"}
                            </p>
                        )}

                        {isVariable && groups.length === 0 && (
                            <p className="mt-6 rounded-xl border border-gold/30 bg-gold/5 p-4 text-xs text-gold" data-testid="product-sizes-loading">
                                We're updating the sizes for this product. Order it by phone at{" "}
                                <a href="tel:+12027134325" className="underline">202-713-4325</a> or check back shortly.
                            </p>
                        )}

                        {isVariable && groups.length > 0 && !variation && (
                            <p className="mt-3 text-xs font-semibold text-gold" data-testid="product-size-required-hint">
                                Please choose a size above to add this to your bag.
                            </p>
                        )}

                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-1 rounded-full border border-leaf/25 bg-moss/50 px-2 py-1.5" data-testid="product-qty-stepper">
                                <button
                                    data-testid="product-qty-minus"
                                    onClick={() => setQtyLocal((q) => Math.max(1, q - 1))}
                                    className="flex h-9 w-9 items-center justify-center rounded-full text-bone hover:text-leaf-bright"
                                    aria-label="Decrease quantity"
                                >
                                    <Minus size={15} />
                                </button>
                                <span className="w-8 text-center font-bold" data-testid="product-qty-value">{qty}</span>
                                <button
                                    data-testid="product-qty-plus"
                                    onClick={() => setQtyLocal((q) => q + 1)}
                                    className="flex h-9 w-9 items-center justify-center rounded-full text-bone hover:text-leaf-bright"
                                    aria-label="Increase quantity"
                                >
                                    <Plus size={15} />
                                </button>
                            </div>
                            <button
                                data-testid="add-to-cart-button"
                                onClick={handleAdd}
                                disabled={outOfStock || (isVariable && !variation)}
                                className="flex flex-1 items-center justify-center gap-3 rounded-full bg-leaf px-8 py-4 font-bold text-ink transition-colors hover:bg-leaf-bright disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none sm:min-w-[220px]"
                            >
                                <ShoppingBag size={18} />
                                {outOfStock ? "Sold out" : isVariable && !variation ? "Select a size first" : "Add to Bag"}
                            </button>
                        </div>

                        {product.sku && (
                            <p className="mt-6 font-mono text-xs text-sage" data-testid="product-sku">SKU: {product.sku}</p>
                        )}

                        {product.description && (
                            <div className="mt-10 border-t border-leaf/12 pt-8" data-testid="product-description">
                                <h2 className="eyebrow text-leaf-bright mb-4">About this product</h2>
                                <div className="prose-dg text-sm text-bone/75" dangerouslySetInnerHTML={{ __html: product.description }} />
                            </div>
                        )}

                        {product.attributes?.length > 0 && (
                            <div className="mt-8 border-t border-leaf/12 pt-8" data-testid="product-attributes">
                                <h2 className="eyebrow text-leaf-bright mb-4">Details</h2>
                                <dl className="space-y-2">
                                    {product.attributes.map((a) => (
                                        <div key={a.name} className="flex gap-4 text-sm">
                                            <dt className="w-32 shrink-0 font-mono text-[11px] uppercase tracking-widest text-sage">{a.name}</dt>
                                            <dd className="text-bone/80">{a.options.join(", ")}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}
                    </div>
                </div>

                <RelatedProducts productId={product.id} category={product.categories?.[0]?.slug} />
            </div>
        </div>
    );
}

function RelatedProducts({ productId, category }) {
    const { data } = useProducts(category ? { category, per_page: 8 } : { per_page: 8 });
    const related = (data?.products || []).filter((p) => p.id !== productId).slice(0, 4);
    if (!related.length) return null;
    return (
        <div className="mt-20" data-testid="related-products">
            <Reveal>
                <h2 className="font-display text-2xl md:text-3xl font-extrabold text-bone">Pairs well with.</h2>
            </Reveal>
            <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
                {related.map((p) => (
                    <ProductCard key={p.id} product={p} />
                ))}
            </div>
        </div>
    );
}
